import { useEffect, useRef } from "react";

// Launches neon fireworks on mount. Self-contained canvas.
export default function Fireworks() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const resize = () => {
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const COLORS = ["#ff1f8a", "#00ffff", "#ffd700", "#b026ff", "#00ff41", "#ff8c1a", "#ff4fb3"];
    const shells = [];
    const particles = [];

    const spawnShell = () => {
      shells.push({
        x: 50 + Math.random() * (canvas.width - 100),
        y: canvas.height + 10,
        vy: -(8 + Math.random() * 4),
        targetY: canvas.height * (0.15 + Math.random() * 0.35),
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
      });
    };

    const explode = (x, y, color) => {
      const count = 40 + Math.floor(Math.random() * 25);
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.2;
        const speed = 2.5 + Math.random() * 3.5;
        particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 1,
          color,
          size: 2 + Math.random() * 1.5,
        });
      }
    };

    let lastSpawn = 0;
    let running = true;
    const loop = (t) => {
      if (!running) return;
      if (t - lastSpawn > 500 + Math.random() * 400) {
        spawnShell();
        lastSpawn = t;
      }

      ctx.fillStyle = "rgba(5,5,10,0.22)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Shells
      for (let i = shells.length - 1; i >= 0; i--) {
        const s = shells[i];
        s.x += 0;
        s.y += s.vy;
        s.vy += 0.12;
        ctx.save();
        ctx.shadowColor = s.color;
        ctx.shadowBlur = 16;
        ctx.fillStyle = s.color;
        ctx.beginPath();
        ctx.arc(s.x, s.y, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        if (s.y <= s.targetY || s.vy >= 0) {
          explode(s.x, s.y, s.color);
          shells.splice(i, 1);
        }
      }

      // Particles
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05;
        p.vx *= 0.99;
        p.life -= 0.014;
        if (p.life <= 0) {
          particles.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 12;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    // Initial burst
    for (let i = 0; i < 3; i++) setTimeout(spawnShell, i * 220);
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      data-testid="fireworks-canvas"
    />
  );
}
